import { createForm } from "@tanstack/solid-form";
import { createFileRoute, Link, useNavigate } from "@tanstack/solid-router";
import { createSignal, Show } from "solid-js";
import { z } from "zod";
import { IconLoaderCircle, IconLogo, IconMail } from "~/components/icons";
import { Button } from "~/components/ui/button";
import { authClient } from "~/lib/auth-client";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Forgot Password | ActShark" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "googlebot", content: "noindex, nofollow" },
    ],
  }),
  component: ForgotPasswordPage,
});

const emailValidator = ({ value }: { value: string }) => {
  const result = z
    .string()
    .min(1, "Email is required")
    .email("Invalid email address")
    .safeParse(value);

  return result.success ? undefined : result.error.issues[0].message;
};

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [error, setError] = createSignal("");
  const [success, setSuccess] = createSignal(false);

  const form = createForm(() => ({
    defaultValues: {
      email: "",
    },
    onSubmit: async ({ value }) => {
      setError("");

      const redirectTo =
        typeof window !== "undefined"
          ? `${window.location.origin}/reset-password?email=${encodeURIComponent(value.email)}`
          : "/reset-password";

      const { error: requestError } = await authClient.requestPasswordReset({
        email: value.email,
        redirectTo,
      });

      if (requestError) {
        setError(requestError.message || "Failed to send reset email");
        return;
      }

      setSuccess(true);
    },
  }));

  const isSubmitting = form.useStore((s) => s.isSubmitting);

  return (
    <div class="min-h-screen lg:grid lg:grid-cols-2">
      <div class="hero-bg relative hidden h-full flex-col p-10 lg:flex">
        <div
          class="pointer-events-none absolute inset-0"
          style="background-image: linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px); background-size: 64px 64px;"
        />

        <Link to="/" class="relative z-20 flex items-center gap-3">
          <IconLogo class="size-9" />
          <div class="flex flex-col">
            <span class="text-base font-semibold leading-none tracking-tight text-white">
              ActShark
            </span>
            <span class="mt-1 text-[10px] font-medium uppercase leading-none tracking-[0.2em] text-white/60">
              accounting
            </span>
          </div>
        </Link>

        <div class="relative z-20 mt-auto space-y-4">
          <h2 class="text-4xl font-bold leading-tight tracking-tight text-white">
            Reset Access,
            <br />
            Keep Momentum
          </h2>
          <p class="text-lg leading-snug text-white/70">
            Enter your account email and we will send a secure password reset
            link.
          </p>
        </div>
      </div>

      <div class="flex min-h-screen flex-col lg:min-h-0">
        <div class="flex items-center justify-between px-6 py-4 lg:hidden">
          <Link to="/" class="flex items-center gap-2">
            <IconLogo class="size-7" fill="currentColor" />
            <div class="flex flex-col">
              <span class="text-base font-semibold leading-none tracking-tight">
                ActShark
              </span>
              <span class="mt-1 text-[10px] font-medium uppercase leading-none tracking-[0.2em] text-muted-foreground">
                accounting
              </span>
            </div>
          </Link>
          <Link
            to="/login"
            class="inline-flex h-9 items-center justify-center rounded-md px-4 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            Back
          </Link>
        </div>

        <div class="flex flex-1 items-center justify-center p-8">
          <div class="mx-auto flex w-full max-w-sm flex-col gap-6">
            <Show
              when={!success()}
              fallback={
                <div class="space-y-5 rounded-xl border bg-card p-6 text-center">
                  <div class="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <IconMail class="size-5" />
                  </div>
                  <div class="space-y-2">
                    <h1 class="text-xl font-semibold tracking-tight">
                      Check your inbox
                    </h1>
                    <p class="text-sm text-muted-foreground">
                      If an account exists for that email, a reset link has been
                      sent.
                    </p>
                  </div>
                  <Button
                    class="w-full"
                    onClick={() => navigate({ to: "/login" })}
                  >
                    Back to login
                  </Button>
                </div>
              }
            >
              <div class="space-y-2 text-center">
                <h1 class="text-2xl font-semibold tracking-tight">
                  Forgot password?
                </h1>
                <p class="text-sm text-muted-foreground">
                  We will email a reset link to your account.
                </p>
              </div>

              <form
                method="post"
                action="/api/auth/forgot-password"
                novalidate
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  form.handleSubmit();
                }}
                class="grid gap-4"
              >
                <form.Field
                  name="email"
                  validators={{ onBlur: emailValidator }}
                >
                  {(field) => (
                    <div class="flex flex-col gap-1.5">
                      <label
                        class="text-sm font-medium leading-none"
                        for="email"
                      >
                        Email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={field().state.value}
                        onInput={(e) =>
                          field().handleChange(e.currentTarget.value)
                        }
                        onBlur={() => field().handleBlur()}
                        placeholder="name@example.com"
                        autocomplete="email"
                        required
                        class="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      />
                      <Show when={field().state.meta.errors[0]}>
                        <p class="text-xs text-destructive">
                          {field().state.meta.errors[0] as string}
                        </p>
                      </Show>
                    </div>
                  )}
                </form.Field>

                <Show when={error()}>
                  <p class="text-xs text-destructive">{error()}</p>
                </Show>

                <Button type="submit" disabled={isSubmitting()}>
                  <Show when={isSubmitting()}>
                    <IconLoaderCircle class="mr-2 size-4 animate-spin" />
                  </Show>
                  Send reset link
                </Button>

                <div class="text-center text-sm">
                  <Link
                    to="/login"
                    class="text-primary underline-offset-4 hover:underline"
                  >
                    Back to login
                  </Link>
                </div>
              </form>
            </Show>
          </div>
        </div>
      </div>

      <Link
        to="/login"
        class="absolute right-8 top-8 hidden h-9 items-center justify-center rounded-md px-4 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground lg:inline-flex"
      >
        Back to login
      </Link>
    </div>
  );
}
